/*
 * Copyright (c) 2026 LSConsulting. All rights reserved.
 * Licensed exclusively for use by the authorized client on this Shopify Store.
 * Unauthorized reproduction, copy, redistribution, alteration, or use without authorization from LSConsulting is strictly prohibited and will be enforced to the fullest extent available under the applicable jurisdiction.
 */
(() => {
  const BASE = 'COP';
  const API_URL = 'https://open.er-api.com/v6/latest/COP';
  const STORAGE_KEY = 'wvx_visual_currency';
  const RATES_KEY = 'wvx_visual_rates';
  const RATES_TTL = 1000 * 60 * 60 * 12;

  const configEl = document.getElementById('VisualCurrencyConfig');
  if (!configEl) return;

  const isEnabled = configEl.getAttribute('data-enabled') === 'true';
  if (!isEnabled) return;

  const labelText = configEl.getAttribute('data-label') || 'Currency';
  const currencyListRaw = configEl.getAttribute('data-currencies') || 'COP';
  const defaultCurrency = (configEl.getAttribute('data-default') || BASE).trim().toUpperCase();
  const marketCurrency = (configEl.getAttribute('data-market-currency') || '').trim().toUpperCase();
  const marketCountry = (configEl.getAttribute('data-market-country') || '').trim().toUpperCase();
  const positionTop = Number(configEl.getAttribute('data-top') || 120);
  const positionRight = Number(configEl.getAttribute('data-right') || 24);
  const mobilePositionTop = Number(configEl.getAttribute('data-mobile-top') || 16);
  const mobilePositionRight = Number(configEl.getAttribute('data-mobile-right') || 12);
  const buttonColor = (configEl.getAttribute('data-button-color') || '#E4592C').trim();

  const currencies = Array.from(
    new Set(
      currencyListRaw
        .split(',')
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean)
    )
  );

  if (!currencies.includes(BASE)) currencies.unshift(BASE);
  const outsideCO = marketCountry && marketCountry !== 'CO';
  const currenciesUI = outsideCO ? currencies.filter((c) => c !== 'COP') : currencies.slice();
  const pickNonCOP = () => currencies.find((c) => c !== 'COP') || BASE;
  const preferredOutside =
    (marketCurrency && marketCurrency !== 'COP' && currencies.includes(marketCurrency) && marketCurrency) ||
    (currencies.includes('USD') ? 'USD' : '') ||
    pickNonCOP();
  const resolveCurrency = (cur) => {
    const requested = String(cur || '').trim().toUpperCase();
    const normalized = currencies.includes(requested) ? requested : BASE;
    if (outsideCO && normalized === 'COP') return preferredOutside;
    return normalized;
  };

  const mount = document.getElementById('VisualCurrencyMount') || document.body;

  const build = () => {
    const wrapper = document.createElement('div');
    wrapper.className = 'vcurrency';
    wrapper.id = 'VisualCurrency';
    wrapper.setAttribute('aria-live', 'polite');
    wrapper.style.setProperty('--vcurrency-button-color', buttonColor);
    const applyPosition = () => {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      wrapper.style.top = 'auto';
      wrapper.style.bottom = `${isMobile ? mobilePositionTop : positionTop}px`;
      wrapper.style.right = `${isMobile ? mobilePositionRight : positionRight}px`;
    };
    applyPosition();
    window.addEventListener('resize', applyPosition);

    const control = document.createElement('div');
    control.className = 'vcurrency__control';
    control.style.position = 'relative';
    control.style.display = 'inline-block';

    const button = document.createElement('button');
    button.className = 'vcurrency__button';
    button.type = 'button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');

    const label = document.createElement('span');
    label.className = 'vcurrency__label';
    label.textContent = defaultCurrency;

    button.appendChild(label);

    const select = document.createElement('select');
    select.id = 'VisualCurrencySelect';
    select.className = 'vcurrency__select';
    select.setAttribute('aria-label', labelText);
    select.style.position = 'absolute';
    select.style.inset = '0';
    select.style.opacity = '0';
    select.style.cursor = 'pointer';
    select.style.width = '100%';
    select.style.height = '100%';
    select.style.border = '0';
    select.style.background = 'transparent';
    select.style.appearance = 'none';

    currenciesUI.forEach((cur) => {
      const opt = document.createElement('option');
      opt.value = cur;
      opt.textContent = cur;
      select.appendChild(opt);
    });
    select.value = defaultCurrency;

    control.appendChild(button);
    control.appendChild(select);
    wrapper.appendChild(control);
    const panel = document.createElement('div');
    panel.className = 'vcurrency__panel';
    panel.hidden = true;

    const list = document.createElement('div');
    currenciesUI.forEach((cur) => {
      const opt = document.createElement('button');
      opt.type = 'button';
      opt.className = 'vcurrency__option';
      opt.setAttribute('data-currency', cur);
      opt.textContent = cur;
      list.appendChild(opt);
    });

    panel.appendChild(list);
    wrapper.appendChild(panel);
    mount.appendChild(wrapper);

    return { wrapper, button, panel, label, select };
  };

  const getLocale = () => document.documentElement.lang || 'es-CO';

  const readCache = () => {
    try {
      const raw = localStorage.getItem(RATES_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !data.rates || !data.timestamp) return null;
      if (Date.now() - data.timestamp > RATES_TTL) return null;
      return data.rates;
    } catch (e) {
      return null;
    }
  };

  const writeCache = (rates) => {
    try {
      localStorage.setItem(RATES_KEY, JSON.stringify({ rates, timestamp: Date.now() }));
    } catch (e) {}
  };

  const fetchRates = async () => {
    const cached = readCache();
    if (cached) return cached;
    try {
      const res = await fetch(API_URL, { credentials: 'omit' });
      if (!res.ok) throw new Error('rate_fetch_failed');
      const json = await res.json();
      if (!json || !json.rates) throw new Error('rate_invalid');
      writeCache(json.rates);
      return json.rates;
    } catch (e) {
      return null;
    }
  };

  const formatAmount = (amount, currency) => {
    try {
      return new Intl.NumberFormat(getLocale(), {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol'
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  };

  const applyCurrency = (currency, rates) => {
    const elements = document.querySelectorAll('[data-money-cents]');
    elements.forEach((el) => {
      const centsRaw = el.getAttribute('data-money-cents');
      const cents = Number(centsRaw);
      if (!Number.isFinite(cents)) return;

      if (!el.hasAttribute('data-money-original')) {
        el.setAttribute('data-money-original', el.textContent || '');
      }

      if (currency === BASE) {
        el.textContent = el.getAttribute('data-money-original') || '';
        return;
      }

      const rate = rates && rates[currency];
      if (!rate) return;
      const amount = (cents / 100) * rate;
      const formatted = formatAmount(amount, currency);
      el.textContent = `${formatted} ${currency}`;
    });

    document.querySelectorAll('[data-currency]').forEach((el) => {
      el.setAttribute('data-currency', currency);
    });
  };

  const setCurrency = async (currency) => {
    const target = resolveCurrency(currency);
    label.textContent = target;
    try { localStorage.setItem(STORAGE_KEY, target); } catch (e) {}

    if (target === BASE) {
      applyCurrency(target, null);
      return;
    }

    const rates = await fetchRates();
    if (!rates) {
      applyCurrency(BASE, null);
      label.textContent = BASE;
      return;
    }
    applyCurrency(target, rates);
  };

  const { button, panel, label, select } = build();

  const setActiveOption = (currency) => {
    const resolved = resolveCurrency(currency);
    panel.querySelectorAll('.vcurrency__option').forEach((opt) => {
      opt.classList.toggle('is-active', opt.getAttribute('data-currency') === resolved);
    });
    if (select) select.value = resolved;
  };

  const togglePanel = (open) => {
    const next = typeof open === 'boolean' ? open : panel.hidden;
    panel.hidden = !next;
    button.setAttribute('aria-expanded', next ? 'true' : 'false');
  };

  button.addEventListener('click', () => togglePanel());
  document.addEventListener('click', (event) => {
    if (!button.closest('.vcurrency')?.contains(event.target)) togglePanel(false);
  });

  panel.addEventListener('click', (event) => {
    const target = event.target.closest('[data-currency]');
    if (!target) return;
    const cur = target.getAttribute('data-currency');
    setCurrency(cur);
    setActiveOption(cur);
    togglePanel(false);
  });

  if (select) {
    select.addEventListener('change', (event) => {
      setCurrency(event.target.value);
      setActiveOption(event.target.value);
    });
  }

  const refreshCurrentCurrency = () => {
    const current = label.textContent || BASE;
    setCurrency(current);
    setActiveOption(current);
  };

  document.addEventListener('shopify:section:load', () => {
    refreshCurrentCurrency();
  });
  document.addEventListener('cart:updated', () => {
    refreshCurrentCurrency();
  });

  let mutationRefreshTimer;
  const observer = new MutationObserver((mutations) => {
    const hasMoneyNodes = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) => {
        if (node.nodeType !== 1) return false;
        const element = node;
        return (
          (typeof element.matches === 'function' && element.matches('[data-money-cents]')) ||
          (typeof element.querySelector === 'function' && !!element.querySelector('[data-money-cents]'))
        );
      })
    );

    if (!hasMoneyNodes) return;
    clearTimeout(mutationRefreshTimer);
    mutationRefreshTimer = setTimeout(() => {
      refreshCurrentCurrency();
    }, 80);
  });
  observer.observe(document.body, { childList: true, subtree: true });

  const initial = (() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
  })();
  const initialSafe = initial && (!outsideCO || initial !== 'COP') ? initial : null;
  const start = resolveCurrency(initialSafe || (outsideCO ? preferredOutside : (currencies.includes(marketCurrency) ? marketCurrency : '')) || defaultCurrency || BASE);
  setCurrency(start);
  setActiveOption(start);
  if (select) select.value = start;
})();
